import { getToken } from './authService';

const API_BASE = process.env.REACT_APP_AUTH_API
  || (process.env.NODE_ENV === 'production' ? '/api/progress' : 'http://localhost:8080/api/progress');

function progressUrl(path: string) {
  const base = API_BASE.replace(/\/auth$/, '/progress').replace(/\/progress$/, '/progress');
  return base.endsWith('/progress') ? `${base}/${path}` : `${API_BASE}/${path}`;
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token
    ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    : { 'Content-Type': 'application/json' };
}

async function fetchWithFallback(url: string, init: RequestInit): Promise<Response> {
  let res = await fetch(url, init);
  if (res.status === 401) {
    const token = getToken();
    if (token) {
      const sep = url.includes('?') ? '&' : '?';
      res = await fetch(`${url}${sep}token=${encodeURIComponent(token)}`, {
        ...init,
        headers: { 'Content-Type': 'application/json' },
      });
    }
  }
  return res;
}

export interface CourseProgress {
  completedLessons: number[];
  lastLessonIndex: number;
  savedCode: Record<number, string>;
  totalInteracted: number;
}

export interface AllCoursesProgress {
  [courseId: string]: {
    totalCompleted: number;
    totalInteracted: number;
    lastLessonIndex: number;
    lastActivity: string;
  };
}

export async function loadCourseProgress(courseId: string): Promise<CourseProgress | null> {
  const token = getToken();
  if (!token) return null;
  try {
    const res = await fetchWithFallback(
      `${progressUrl('load.php')}?courseId=${encodeURIComponent(courseId)}`,
      { method: 'GET', headers: authHeaders() },
    );
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.success) return null;
    return {
      completedLessons: data.completedLessons ?? [],
      lastLessonIndex: data.lastLessonIndex ?? 0,
      savedCode: data.savedCode ?? {},
      totalInteracted: data.totalInteracted ?? 0,
    };
  } catch {
    return null;
  }
}

export async function loadAllProgress(): Promise<AllCoursesProgress | null> {
  const token = getToken();
  if (!token) return null;
  try {
    const res = await fetchWithFallback(
      progressUrl('load.php'),
      { method: 'GET', headers: authHeaders() },
    );
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.success) return null;
    return data.courses ?? {};
  } catch {
    return null;
  }
}

export async function saveProgress(
  courseId: string,
  lessonIndex: number,
  completed: boolean,
  code?: string,
): Promise<boolean> {
  const token = getToken();
  if (!token) return false;
  try {
    const res = await fetchWithFallback(
      progressUrl('save.php'),
      {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ courseId, lessonIndex, completed, code }),
      },
    );
    return res.ok;
  } catch {
    return false;
  }
}

export async function trackVideoDecile(
  courseId: string,
  lessonIndex: number,
  decile: number,
): Promise<boolean> {
  const token = getToken();
  if (!token) return false;
  try {
    const res = await fetchWithFallback(
      progressUrl('video-watch.php'),
      {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ courseId, lessonIndex, decile }),
      },
    );
    return res.ok;
  } catch {
    return false;
  }
}
